import { isSingletonElementKind } from "@/types/intake";
import type { TIntakePage, TIntakeTreeElement } from "@/types/intake";

/**
 * Path identifying a container's children list within a page. Each segment is
 * the id of a containing element; for ``columns``, the segment is suffixed
 * with ``:<columnId>`` so we can disambiguate sibling column children lists.
 *
 * - ``[]`` → the page's root ``elements`` array.
 * - ``["group-1"]`` → the children of the ``field_group`` with id ``group-1``.
 * - ``["cols-1:col-a"]`` → the children of the ``col-a`` column inside the
 *   ``columns`` container ``cols-1``.
 */
export type ContainerPath = string[];

const COLUMN_SEPARATOR = ":";

/** Encode a column-children segment ``<containersId>:<columnId>``. */
export function columnSegment(containerId: string, columnId: string): string {
  return `${containerId}${COLUMN_SEPARATOR}${columnId}`;
}

interface ParsedSegment {
  containerId: string;
  columnId: string | null;
}

function parseSegment(segment: string): ParsedSegment {
  const idx = segment.indexOf(COLUMN_SEPARATOR);
  if (idx === -1) return { containerId: segment, columnId: null };
  return {
    containerId: segment.slice(0, idx),
    columnId: segment.slice(idx + 1),
  };
}

/**
 * Walk a page from the root and return the children array addressed by
 * ``path``. Returns ``null`` if the path is unreachable (broken container id
 * or missing column). The caller can splice / push on the returned array to
 * mutate (only when it operates on the same page reference passed in).
 */
export function getChildrenAtPath(
  page: TIntakePage,
  path: ContainerPath,
): TIntakeTreeElement[] | null {
  let children: TIntakeTreeElement[] = page.elements;
  for (const segment of path) {
    const { containerId, columnId } = parseSegment(segment);
    const next = children.find((el) => el.id === containerId);
    if (!next) return null;
    if (next.kind === "field_group") {
      if (columnId !== null) return null;
      children = next.children;
      continue;
    }
    if (next.kind === "columns") {
      if (columnId === null) return null;
      const column = next.columns.find((c) => c.id === columnId);
      if (!column) return null;
      children = column.children;
      continue;
    }
    return null;
  }
  return children;
}

interface TreeLocation {
  path: ContainerPath;
  index: number;
}

export interface ElementWithLocation {
  element: TIntakeTreeElement;
  location: TreeLocation;
}

/**
 * Yield every element in the page in document order, paired with its location
 * (containing path + index inside that container).
 */
export function* iterTree(page: TIntakePage): Generator<ElementWithLocation> {
  yield* iterContainer(page.elements, []);
}

function* iterContainer(
  children: TIntakeTreeElement[],
  path: ContainerPath,
): Generator<ElementWithLocation> {
  for (let index = 0; index < children.length; index += 1) {
    const element = children[index];
    if (!element) continue;
    yield { element, location: { path, index } };
    if (element.kind === "field_group") {
      yield* iterContainer(element.children, [...path, element.id]);
    } else if (element.kind === "columns") {
      for (const column of element.columns) {
        yield* iterContainer(column.children, [...path, columnSegment(element.id, column.id)]);
      }
    }
  }
}

/**
 * Find a tree element by id anywhere on a page. Returns the element plus its
 * location (parent path + sibling index) so callers can mutate the right list.
 */
export function findElementById(page: TIntakePage, id: string): ElementWithLocation | null {
  for (const entry of iterTree(page)) {
    if (entry.element.id === id) return entry;
  }
  return null;
}

/**
 * Walk all column ids hosted under ``element`` (including ``element`` itself
 * if it's a container). Used by the reparent guard so we can refuse a drop
 * that would form a cycle.
 */
export function collectSubtreeIds(element: TIntakeTreeElement): Set<string> {
  const ids = new Set<string>();
  const stack: TIntakeTreeElement[] = [element];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    ids.add(node.id);
    if (node.kind === "field_group") {
      for (const child of node.children) stack.push(child);
    } else if (node.kind === "columns") {
      for (const column of node.columns) {
        ids.add(column.id);
        for (const child of column.children) stack.push(child);
      }
    }
  }
  return ids;
}

/**
 * True iff ``element`` is — or contains, anywhere in its subtree — a
 * singleton kind (program, request category). Duplicating such a subtree
 * would mint a second instance of a kind the schema allows at most once.
 */
export function subtreeContainsSingleton(element: TIntakeTreeElement): boolean {
  const stack: TIntakeTreeElement[] = [element];
  while (stack.length > 0) {
    const node = stack.pop();
    if (!node) continue;
    if (isSingletonElementKind(node.kind)) return true;
    if (node.kind === "field_group") {
      for (const child of node.children) stack.push(child);
    } else if (node.kind === "columns") {
      for (const column of node.columns) {
        for (const child of column.children) stack.push(child);
      }
    }
  }
  return false;
}

/** True iff ``path`` traverses through any id in ``forbidden``. */
export function pathTouches(path: ContainerPath, forbidden: ReadonlySet<string>): boolean {
  for (const segment of path) {
    const { containerId, columnId } = parseSegment(segment);
    if (forbidden.has(containerId)) return true;
    if (columnId && forbidden.has(columnId)) return true;
  }
  return false;
}

/** Compare two paths for structural equality. */
export function pathEquals(a: ContainerPath, b: ContainerPath): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

/** Encode a path as a stable string for use as a DnD scope. */
export function encodePath(path: ContainerPath): string {
  return path.join("/");
}

/** Decode a path previously serialized by ``encodePath``. */
export function decodePath(serialized: string): ContainerPath {
  if (!serialized) return [];
  return serialized.split("/");
}
